import { useLocale } from '@va/localization';
import { DatePickerShortcut, DateRangeKey } from '@va/types/time';
import { DATEPICKER_DEFAULT_SHORTCUTS, findMatchingShortcut } from '@va/util/helpers';
import { useSetState } from '@va/util/hooks';
import { Dayjs } from 'dayjs';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo } from 'react';
import { useCalendar } from '../hooks';
import { CalendarDate, CalendarObj, CalendarState, PanelState, PanelView } from '../types';
import { formatDatepickerValue, getBetweenRange, sortDates } from '../utils';

type DatePickerContextType = DatePickerContextProviderProps & {
  shortcuts: DatePickerShortcut[];
  applyShortcut: (start: Dayjs, end: Dayjs, shortcutKey?: DateRangeKey) => void;
  state: DatePickerV2State;
  calendar: { previous: CalendarObj; next: CalendarObj };
  calendarState: CalendarState;
  panelState: PanelState;
  isBetweenRange: (date: CalendarDate) => boolean;
  setDate: (date: CalendarDate, onAutoApply?: () => void) => void;
  isDirty: boolean;
  applyDate: () => void;
  prefixValue?: DatePickerShortcut | null;
  pickerValue: string | null;
  resetPanelState: () => void;
};

export type DatePickerV2State = {
  pendingValue: [Dayjs, Dayjs] | [];
  /** Tracked separately from the dates because several shortcuts can resolve to the same range (e.g. "Today" and "This month" on the 1st) */
  pendingShortcutKey: DateRangeKey | null;
  previous: Dayjs | null;
  next: Dayjs | null;
};

export const DatePickerContext = createContext<DatePickerContextType>({
  overlay: false,
  shortcuts: DATEPICKER_DEFAULT_SHORTCUTS,
  onChange: () => {},
  applyShortcut: () => {},
  value: null,
  state: {
    pendingValue: [],
    pendingShortcutKey: null,
    previous: null,
    next: null,
  },
  calendar: { previous: {}, next: {} } as { previous: CalendarObj; next: CalendarObj },
  calendarState: {} as CalendarState,
  panelState: {
    previous: PanelView.Calendar,
    next: PanelView.Calendar,
  },
  isBetweenRange: () => false,
  setDate: () => {},
  isDirty: false,
  applyDate: () => {},
  resetPanelState: () => {},
  prefixValue: null,
  pickerValue: null,
});

export type DatePickerContextProviderProps = {
  placeholder?: string;
  overlay?: boolean;
  shortcuts?: DatePickerShortcut[];
  value: [Dayjs, Dayjs] | null;
  trackedDataStartDate?: Dayjs;
  shouldDisablePastDates?: boolean;
  tooltipContent?: string;
  onChange: (date: [Dayjs, Dayjs], shortcutKey?: DateRangeKey) => void;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  size?: 'small' | 'medium';
  showPrefix?: boolean;
  onHover?: () => void;
  onClick?: () => void;
  autoApply?: boolean;
};

export const DatePickerContextProvider = ({
  shouldDisablePastDates = false,
  onChange = () => undefined,
  trackedDataStartDate,
  autoApply = false,
  overlay = false,
  shortcuts = DATEPICKER_DEFAULT_SHORTCUTS,
  size = 'medium',
  tooltipContent,
  placeholder,
  children,
  minDate,
  maxDate,
  onClick,
  onHover,
  value,
  showPrefix = true,
}: PropsWithChildren<DatePickerContextProviderProps>) => {
  const initialState = useMemo<DatePickerV2State>(
    () => ({
      pendingValue: [...(value?.map((d) => d.clone()) ?? [])] as [Dayjs, Dayjs],
      pendingShortcutKey: null,
      previous: null,
      next: null,
    }),
    [value],
  );

  const [state, setState] = useSetState(initialState);
  const {
    calendar,
    state: calendarState,
    setState: setCalendarState,
    panelState,
    resetPanelState,
    updateCalendar,
  } = useCalendar({
    maxDate,
    minDate,
    shouldDisablePastDates,
    initialState: { previous: value?.[0], next: value?.[1] },
  });

  const prefixValue = useMemo(
    () => value && findMatchingShortcut(shortcuts, value, trackedDataStartDate),
    [shortcuts, value, trackedDataStartDate],
  );
  const { locale } = useLocale();

  // A manually picked range that matches a shortcut is treated as that shortcut (the same one shown as prefix). A clicked shortcut always wins, since several can give the same range.
  const getShortcutKey = useCallback(
    (range: [Dayjs, Dayjs], selectedShortcutKey?: DateRangeKey | null) =>
      selectedShortcutKey ?? findMatchingShortcut(shortcuts, range, trackedDataStartDate)?.key,
    [shortcuts, trackedDataStartDate],
  );
  const pickerValue = useMemo(
    () => (value ? formatDatepickerValue({ previous: value[0], next: value[1], locale }) : null),
    [value, locale],
  );

  const reset = useCallback(() => {
    setState({ previous: null, next: null });
  }, [setState]);

  const checkLimit = useCallback((startedDate: Dayjs, endedDate: Dayjs): [Dayjs, Dayjs] => {
    // VISA-11309 and VISA-11325 - allow the user to set any datepicker value.
    return [startedDate, endedDate];
  }, []);

  const applyShortcut = useCallback(
    (start: Dayjs, end: Dayjs, shortcutKey?: DateRangeKey) => {
      const value = checkLimit(start, end);

      setState({ pendingValue: value, pendingShortcutKey: shortcutKey ?? null });
      if (autoApply) {
        onChange(value, shortcutKey);
      }
      reset();
      updateCalendar(...value);
      resetPanelState();
    },
    [checkLimit, autoApply, reset, updateCalendar, resetPanelState, setState, onChange],
  );

  const isBetweenRange = useCallback(
    (date: CalendarDate) => {
      const { previous, pendingValue } = state;

      if ((previous && autoApply) || !value) return false;
      let startedDate, endedDate;

      if (autoApply) {
        [startedDate, endedDate] = value;
      } else if (pendingValue.length) {
        [startedDate, endedDate] = pendingValue;
      }

      if (startedDate && endedDate) {
        return getBetweenRange(date, { previous: startedDate, next: endedDate });
      }

      return false;
    },
    [autoApply, state, value],
  );

  const setDate = useCallback(
    (date: CalendarDate, onAutoApply?: () => void) => {
      const { previous, pendingValue } = state;

      if (!previous) {
        const [, oldNext] = pendingValue as [Dayjs, Dayjs];

        setState({ previous: date, pendingValue: sortDates([date, oldNext]), pendingShortcutKey: null });
        setCalendarState((prevCalendarState) => {
          if (prevCalendarState.next.isSame(date, 'month'))
            return { previous: prevCalendarState.next, next: date.add(1, 'month') };

          return { previous: date };
        });

        return;
      }

      const [startDate, endDate] = sortDates([previous, date]);

      setState({ next: date, pendingValue: [startDate, endDate], pendingShortcutKey: null });

      if (!previous.isSame(date, 'month')) {
        setCalendarState({ previous: startDate, next: endDate });
      }

      if (autoApply) {
        onChange([startDate, endDate], getShortcutKey([startDate, endDate]));
        onAutoApply?.();
      }

      reset();
    },
    [autoApply, getShortcutKey, onChange, reset, setCalendarState, setState, state],
  );

  const isDirty = useMemo(() => {
    if (!state.pendingValue.length || !value) return true;

    const [start, end] = state.pendingValue;
    const [initial, final] = value;

    return !(start.isSame(initial) && end.isSame(final));
  }, [value, state.pendingValue]);

  const applyDate = useCallback(() => {
    const { pendingValue, pendingShortcutKey } = state;
    if (!pendingValue.length) return;

    onChange(pendingValue, getShortcutKey(pendingValue, pendingShortcutKey));

    reset();
  }, [getShortcutKey, onChange, reset, state]);

  // Keep pendingValue in sync with `value`
  useEffect(() => {
    setState({ pendingValue: [...(value?.map((d) => d.clone()) ?? [])] as [Dayjs, Dayjs], pendingShortcutKey: null });
  }, [setState, value]);

  return (
    <DatePickerContext.Provider
      value={{
        size,
        shouldDisablePastDates,
        trackedDataStartDate,
        showPrefix,
        onClick,
        onHover,
        applyShortcut,
        state,
        calendar,
        isBetweenRange,
        setDate,
        isDirty,
        applyDate,
        prefixValue,
        autoApply,
        overlay,
        shortcuts,
        onChange,
        minDate,
        maxDate,
        tooltipContent,
        calendarState,
        panelState,
        pickerValue,
        placeholder,
        value,
        resetPanelState,
      }}
    >
      {children}
    </DatePickerContext.Provider>
  );
};

export const useDatePickerContext = () => useContext(DatePickerContext);
